import { useCallback, useEffect, useState } from "react";

import clsx from "clsx";

import { useTranslation } from "react-i18next";

import AddIcon from "assets/icons/filled/add.svg?react";
import CloseIcon from "assets/icons/filled/close-circle.svg?react";
import TickIcon from "assets/icons/filled/tick.svg?react";
import MailIcon from "assets/icons/outlined/mail.svg?react";
import UploadIcon from "assets/icons/outlined/upload.svg?react";

import { FormBlock } from "shared/Components";

import { useDebounce } from "shared/hooks";
import { AvatarWrapper, PersonItem, RefereeCard } from "shared/templates/InviteTemplate/style";
import type { InvitationPersonaModel } from "shared/types";
import { DividerWithText, Input, Text } from "shared/uikit";
import { ButtonSizesEnum, TextStylesEnum, TextVariantsEnum } from "shared/uikit/types";

import { validateEmail } from "utils/serviceUtils/validators";

import { DigitCircle } from "./style";

export interface InviteModalContentProps {
	searchHeadline: string;
	uploadTitle: string;
	onChangeKeyword: (val: string) => void;
	suggestedList: Partial<InvitationPersonaModel>[];
	onChangeSelected: (invitations: Partial<InvitationPersonaModel>[]) => void;
	removeWrapperSpace?: boolean;
	supportBulk?: boolean;
}

const InviteModalContent: React.FC<InviteModalContentProps> = ({
	searchHeadline,
	uploadTitle,
	onChangeKeyword,
	suggestedList,
	onChangeSelected,
	removeWrapperSpace,
	supportBulk
}) => {
	const { t } = useTranslation();

	const [keyword, setKeyword] = useState("");

	const debouncedKeyword = useDebounce(keyword, 250);

	const [selected, setSelected] = useState<Partial<InvitationPersonaModel>[]>([]);

	useEffect(() => {
		onChangeKeyword(debouncedKeyword);
	}, [debouncedKeyword, onChangeKeyword]);

	const addSelected = useCallback(
		(personas: Partial<InvitationPersonaModel> | Partial<InvitationPersonaModel>[]) => {
			const currentSelected = [...selected];
			if (Array.isArray(personas)) {
				personas.forEach(persona => {
					if (!currentSelected.some(x => x.email?.toLowerCase() === persona.email?.toLowerCase())) {
						currentSelected.push(persona);
					}
				});
			} else if (!currentSelected.some(x => x.email?.toLowerCase() === personas.email?.toLowerCase())) {
				currentSelected.push(personas);
			}
			setSelected(currentSelected);
			onChangeSelected(currentSelected);
		},
		[selected, onChangeSelected]
	);

	const removeSelected = useCallback(
		(email: string) => {
			const index = selected.findIndex(x => x.email?.toLowerCase() === email?.toLowerCase());
			if (index > -1) {
				const newSelected = [...selected];
				newSelected.splice(index, 1);
				setSelected(newSelected);
				onChangeSelected(newSelected);
			}
		},
		[selected, onChangeSelected]
	);

	const checkIfSelected = useCallback(
		(email: string) => {
			return selected.some(x => x.email?.toLowerCase() === email?.toLowerCase());
		},
		[selected]
	);

	const customRender = useCallback(
		(item: Partial<InvitationPersonaModel>) => {
			const isSelected = checkIfSelected(`${item.email}`);

			return (
				<PersonItem>
					<div>
						{item.avatarUrl && (
							<AvatarWrapper className={"mr-2"} style={{ backgroundImage: `url(${item.avatarUrl})` }} />
						)}
						<Text style={TextStylesEnum.Default} variants={TextVariantsEnum.Caption3}>
							{item?.name || item?.email}
						</Text>
					</div>
					<div className="cursor-pointer ml-1">
						{isSelected ? (
							<TickIcon className="w-[1rem] h-[1rem] svg-first-path:stroke-gray-500 svg-first-path:fill-gray-500" />
						) : (
							<AddIcon className="w-[1rem] h-[1rem] svg-first-path:stroke-gray-500 svg-first-path:fill-gray-500" />
						)}
					</div>
				</PersonItem>
			);
		},
		[checkIfSelected]
	);

	const getOptionLabel = useCallback(
		(option: Partial<InvitationPersonaModel>): string => option?.name || option.email || "",
		[]
	);

	const handleChangeName = event => {
		if (event?.target) {
			const notAllowedCharacters = [",", ";", ":", " ", "\t", "\n"];
			const val = event?.target?.value || "";
			const notAllowedCharacter = notAllowedCharacters.find(ch => val.includes(ch));
			if (!!notAllowedCharacter) {
				// multiple emails
				const emails: string[] = val.split(notAllowedCharacter);
				const finalEmailList: Partial<InvitationPersonaModel>[] = [];
				emails.forEach(email => {
					if (validateEmail(email)) {
						finalEmailList.push({
							name: email,
							email,
							avatarUrl: ""
						});
					}
				});

				addSelected(finalEmailList);

				setKeyword("");
			} else {
				setKeyword(event?.target?.value || "");
			}
		}
	};

	return (
		<div
			className={clsx(
				"flex flex-col h-full md:flex-row md:justify-between md:gap-8",
				!removeWrapperSpace && "p-6 pb-0 "
			)}
		>
			<div className="flex flex-col flex-1">
				<div className="flex flex-col gap-6">
					<FormBlock label={searchHeadline} labelClassname="mb-2">
						<div className="flex items-center justify-between">
							<div className={"w-full"}>
								<Input
									search
									defaultValue={""}
									inputValue={keyword}
									size={ButtonSizesEnum.UNDER_MD}
									onChange={(_, option) => {
										if (option?.email) {
											const isSelected = checkIfSelected(`${option.email}`);
											!isSelected && addSelected(option);
											setKeyword("");
										}
									}}
									options={suggestedList}
									placeholder={t("invitation:enter_email_or_username")}
									className="w-full"
									LeftSideIcon={<MailIcon />}
									customDropdownItemRender={customRender}
									onInputChange={handleChangeName}
									getOptionLabel={getOptionLabel}
									id="enter-name-email"
								/>
							</div>
						</div>
					</FormBlock>
					{!!selected?.length && (
						<FormBlock label="Selected members & emails" labelClassname="mb-2">
							{selected.map((item, index) => (
								<div className="mr-2 mb-3 inline-block align-top" key={index}>
									<RefereeCard>
										{item.avatarUrl && (
											<AvatarWrapper className={"mr-2"} style={{ backgroundImage: `url(${item.avatarUrl})` }} />
										)}
										<Text style={TextStylesEnum.Default} variants={TextVariantsEnum.Caption3}>
											{item?.name || item?.email}
										</Text>
										<div
											id={`remove-selected-${index + 1}`}
											className="cursor-pointer ml-1"
											onClick={() => removeSelected(`${item.email}`)}
										>
											<CloseIcon className="w-[1rem] h-[1rem] svg-first-path:stroke-gray-400 svg-first-path:fill-gray-400" />
										</div>
									</RefereeCard>
								</div>
							))}
						</FormBlock>
					)}
				</div>
			</div>
			{supportBulk && (
				<>
					<DividerWithText vertical text={"OR"} className="mb-5 pb-3 md:mb-0" />
					<div className="flex flex-col flex-1">
						<Text variants={TextVariantsEnum.H7} className="mb-3">
							{uploadTitle}
						</Text>
						<div className="flex items-center mb-4">
							<DigitCircle className="mr-3">
								<Text style={TextStylesEnum.Hint} variants={TextVariantsEnum.BodyMedium}>
									1
								</Text>
							</DigitCircle>
							<div className="cursor-pointer flex items-center">
								<Text style={TextStylesEnum.Primary} variants={TextVariantsEnum.BodyMedium}>
									<b>Download template</b>
								</Text>
								<div className="ml-2">
									<UploadIcon className="w-5 h-5 svg-paths:fill-primary-500" />
								</div>
							</div>
						</div>
						<div className="flex items-center mb-4">
							<DigitCircle className="mr-3">
								<Text style={TextStylesEnum.Hint} variants={TextVariantsEnum.BodyMedium}>
									2
								</Text>
							</DigitCircle>
							<Text style={TextStylesEnum.Hint} variants={TextVariantsEnum.BodyMedium}>
								Fill in the template
							</Text>
						</div>
						<div className="flex items-center">
							<DigitCircle className="mr-3">
								<Text style={TextStylesEnum.Hint} variants={TextVariantsEnum.BodyMedium}>
									3
								</Text>
							</DigitCircle>
							<Text style={TextStylesEnum.Hint} variants={TextVariantsEnum.BodyMedium}>
								Upload the filled template
							</Text>
						</div>
					</div>
				</>
			)}
		</div>
	);
};

export default InviteModalContent;
